import { Link, Outlet, useRouterState } from "@tanstack/react-router";
import { ArrowRight, BookOpen, Clock } from "lucide-react";
import type { Article } from "@/lib/content-blocks";
import type { AreaBase, AreaRoute } from "@/lib/article-routes";

/** A sidebar group. `/tutorials` has three; `/geo` has one, unlabelled. */
export type ArticleGroup = { title?: string; items: Article[] };

/**
 * Sidebar + content shell shared by /tutorials and /geo.
 *
 * The route that uses this deliberately has no `head()`: it is a layout route,
 * and emitting SEO here would add a second <link rel="canonical"> on every child
 * page on top of the child's own.
 */
export function ArticleLayout({
  areaLabel,
  base,
  articleRoute,
  groups,
}: {
  areaLabel: string;
  base: AreaBase;
  articleRoute: AreaRoute;
  groups: ArticleGroup[];
}) {
  const pathname = useRouterState({ select: (s) => s.location.pathname });

  return (
    <div className="px-6 pt-12 pb-24">
      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-10">
        {/* Sidebar */}
        <aside className="lg:sticky lg:top-24 lg:self-start lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto pr-2">
          <Link
            to={base}
            className="flex items-center gap-2 text-sm font-medium text-foreground mb-6 hover:text-primary transition-colors"
          >
            <BookOpen className="h-4 w-4" />
            {areaLabel}
          </Link>

          <nav className="space-y-7">
            {groups.map((group, gi) => (
              <div key={group.title ?? gi}>
                {group.title && (
                  <p className="text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground mb-3">
                    {group.title}
                  </p>
                )}
                <ul className="space-y-1 border-l border-border/60">
                  {group.items.map((a) => {
                    const active = pathname === `${base}/${a.slug}`;
                    return (
                      <li key={a.slug}>
                        <Link
                          to={articleRoute}
                          params={{ slug: a.slug }}
                          className={`-ml-px block border-l-2 pl-3 py-1.5 text-sm leading-snug transition-colors ${
                            active
                              ? "border-primary text-foreground font-medium"
                              : "border-transparent text-muted-foreground hover:text-foreground hover:border-border"
                          }`}
                        >
                          {a.title}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <div className="min-w-0 lg:px-12 xl:px-20">
          <Outlet />
        </div>
      </div>
    </div>
  );
}

/** The card grid used on each area's index page. */
export function ArticleCardGrid({
  articleRoute,
  items,
}: {
  articleRoute: AreaRoute;
  items: Article[];
}) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {items.map((a) => (
        <Link
          key={a.slug}
          to={articleRoute}
          params={{ slug: a.slug }}
          className="group rounded-xl border border-border/60 bg-card/40 hover:bg-card/70 hover:border-border transition-all p-5"
        >
          <h3 className="text-base font-medium leading-snug mb-2 group-hover:text-primary transition-colors">
            {a.title}
          </h3>
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4">{a.intro}</p>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3 w-3" />
              {a.estimatedTime} · {a.difficulty}
            </span>
            <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
          </div>
        </Link>
      ))}
    </div>
  );
}
